<template>
  <div class="route-dashboard max-container">
    <div class="route-dashboard-inner">
      <section class="route-welcome" aria-labelledby="route-welcome-title">
        <div class="route-welcome-copy">
          <span class="route-eyebrow">Your approved assortment</span>
          <h1 id="route-welcome-title">{{ greeting }}, {{ accountName }}</h1>
          <p>
            Reorder proven products, spot opportunities across your catalog,
            and see what is changing for your stores.
          </p>
        </div>

        <router-link class="route-primary-action" to="/">
          Browse your catalog
          <span aria-hidden="true">→</span>
        </router-link>
      </section>

      <section class="route-metrics" aria-label="Catalog overview">
        <article v-for="metric in metrics" :key="metric.label" class="route-metric">
          <span class="route-metric-value">{{ metric.value }}</span>
          <span class="route-metric-label">{{ metric.label }}</span>
          <span class="route-metric-note">{{ metric.note }}</span>
        </article>
      </section>

      <section class="route-opportunities" aria-labelledby="opportunities-title">
        <div class="route-section-heading">
          <div>
            <span class="route-section-kicker">Act on what matters</span>
            <h2 id="opportunities-title">Opportunities</h2>
            <p>Useful signals from products already available to your account.</p>
          </div>
        </div>

        <div class="route-opportunity-grid">
          <router-link
            v-for="opportunity in opportunities"
            :key="opportunity.title"
            :to="opportunity.to"
            class="route-opportunity"
            :class="`is-${opportunity.tone}`"
          >
            <span class="route-opportunity-count">{{ opportunity.count }}</span>
            <div>
              <h3>{{ opportunity.title }}</h3>
              <p>{{ opportunity.description }}</p>
            </div>
            <span class="route-opportunity-arrow" aria-hidden="true">→</span>
          </router-link>
        </div>
      </section>

      <section
        v-for="section in productSections"
        :key="section.id"
        class="route-product-section"
        :aria-labelledby="`${section.id}-title`"
      >
        <div class="route-section-heading">
          <div>
            <span class="route-section-kicker">{{ section.kicker }}</span>
            <h2 :id="`${section.id}-title`">{{ section.title }}</h2>
            <p>{{ section.description }}</p>
          </div>
          <router-link :to="section.to" class="route-text-link">
            View all <span aria-hidden="true">→</span>
          </router-link>
        </div>

        <div v-if="section.loading" class="route-product-grid">
          <div v-for="item in 6" :key="item" class="route-product-skeleton">
            <span></span><span></span><span></span>
          </div>
        </div>

        <div v-else-if="section.items.length" class="route-product-grid">
          <button
            v-for="(item, index) in section.items.slice(0, 6)"
            :key="item.id || `${section.id}-${index}`"
            class="route-product-card"
            type="button"
            @click="gotToPage(item, index, section.id)"
          >
            <span class="route-product-badge">{{ section.badge }}</span>
            <span class="route-product-image">
              <img :src="getImage(item)" :alt="item.name" />
            </span>
            <span class="route-product-brand">{{ getBrand(item) }}</span>
            <strong>{{ item.name }}</strong>
            <span class="route-product-signal">{{ section.signal }}</span>
          </button>
        </div>

        <div v-else class="route-empty-state">
          <strong>{{ section.emptyTitle }}</strong>
          <span>{{ section.emptyText }}</span>
        </div>
      </section>

      <div class="route-split-sections">
        <section class="route-chain-panel" aria-labelledby="chain-title">
          <span class="route-section-kicker">Across your chain</span>
          <h2 id="chain-title">What other locations are choosing</h2>
          <p class="route-chain-intro">
            Use activity from related stores to find eligible products your
            location may be overlooking.
          </p>

          <div class="route-chain-list">
            <button
              v-for="(item, index) in popularOtherLocations.data.slice(0, 4)"
              :key="item.id || index"
              type="button"
              @click="gotToPage(item, index, 'popular-other-locations')"
            >
              <span class="route-chain-image"><img :src="getImage(item)" :alt="item.name" /></span>
              <span>
                <small>Popular at other locations</small>
                <strong>{{ item.name }}</strong>
                <em>{{ getBrand(item) }}</em>
              </span>
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <div v-if="!popularOtherLocations.loading && !popularOtherLocations.data.length" class="route-inline-empty">
            Chain-level recommendations will appear here as activity builds.
          </div>
        </section>

        <section class="route-price-panel" aria-labelledby="price-title">
          <span class="route-section-kicker">Timely value</span>
          <h2 id="price-title">Price drops for you</h2>
          <p class="route-chain-intro">
            Eligible products with current clearance pricing or promotions.
          </p>

          <div class="route-price-list">
            <button
              v-for="(item, index) in priceDrops.data.slice(0, 4)"
              :key="item.id || index"
              type="button"
              @click="gotToPage(item, index, 'price-drops')"
            >
              <span class="route-price-image"><img :src="getImage(item)" :alt="item.name" /></span>
              <span>
                <small>Current opportunity</small>
                <strong>{{ item.name }}</strong>
                <em>{{ getBrand(item) }}</em>
              </span>
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <div v-if="!priceDrops.loading && !priceDrops.data.length" class="route-inline-empty">
            No catalog-specific price opportunities are available right now.
          </div>
        </section>
      </div>

      <section class="route-catalog-explore" aria-labelledby="catalog-explore-title">
        <div class="route-section-heading">
          <div>
            <span class="route-section-kicker">Stay within your assortment</span>
            <h2 id="catalog-explore-title">Explore your catalog</h2>
            <p>Categories represented in the products available to this account.</p>
          </div>
          <router-link to="/categories" class="route-text-link">
            All categories <span aria-hidden="true">→</span>
          </router-link>
        </div>

        <div v-if="categories.loading" class="route-category-grid">
          <div v-for="item in 6" :key="item" class="route-category-skeleton"></div>
        </div>
        <div v-else class="route-category-grid">
          <router-link
            v-for="category in catalogCategories"
            :key="category.id || category.slug"
            :to="slugUrl(`category/${category.slug}`)"
            class="route-category"
          >
            <span>
              <img
                :src="category.image ? imageBaseUrl(category.image) : '/images/no-image.png'"
                :alt="category.name"
              />
            </span>
            <strong>{{ category.name }}</strong>
          </router-link>
        </div>
      </section>

      <section class="route-brands" aria-labelledby="brands-title">
        <div>
          <span class="route-section-kicker">Your assortment</span>
          <h2 id="brands-title">Brands available to you</h2>
          <p>Brand names represented across the eligible products shown above.</p>
        </div>
        <div class="route-brand-list">
          <span v-for="brand in availableBrands" :key="brand">{{ brand }}</span>
          <span v-if="!availableBrands.length" class="route-brand-placeholder">
            Available brands will appear when catalog data loads.
          </span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Categories from "@/modules/Categories";
import Collections from "@/modules/Collections";
import Segments from "@/modules/Segments";
import { computed, onMounted, reactive } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

const createProductState = () => reactive({ loading: true, data: [] });

export default {
  name: "RouteLanding",
  setup() {
    const router = useRouter();
    const store = useStore();
    const { ProductClicked } = Segments();
    const { getCollection } = Collections();
    const { categories, getCategories, topSellProducts, getTopSellProducts } = Categories();

    const trendingAcrossChain = createProductState();
    const newToCatalog = createProductState();
    const popularOtherLocations = createProductState();
    const priceDrops = createProductState();

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    const accountName =
      localStorage.getItem("company_name") ||
      localStorage.getItem("user_company") ||
      localStorage.getItem("username") ||
      "Your store";

    const loadCollection = async (slug, state, limit = 12) => {
      state.loading = true;
      try {
        state.data = (await getCollection(slug, limit)) || [];
      } catch (error) {
        console.error(`Failed to load ${slug}:`, error);
        state.data = [];
      } finally {
        state.loading = false;
      }
    };

    onMounted(() => {
      getCategories();
      getTopSellProducts();
      loadCollection("trending", trendingAcrossChain);
      loadCollection("new-arrivals", newToCatalog);
      loadCollection("staff-picks", popularOtherLocations, 6);
      loadCollection("clearance", priceDrops, 6);
    });

    const approvedProductCount = computed(() => {
      const allow = store.getters.getCatalogEntitlement?.allow;
      const ids = new Set([...(allow?.product_ids || []), ...(allow?.variant_product_ids || [])]);
      return ids.size || store.getters.getProductCount || "—";
    });

    const metrics = computed(() => [
      { value: approvedProductCount.value, label: "Approved products", note: "Your eligible assortment" },
      { value: newToCatalog.loading ? "…" : newToCatalog.data.length, label: "New to your catalog", note: "Recently added products" },
      { value: trendingAcrossChain.loading ? "…" : trendingAcrossChain.data.length, label: "Trending now", note: "Within your assortment" },
      { value: priceDrops.loading ? "…" : priceDrops.data.length, label: "Price opportunities", note: "Eligible clearance items" },
    ]);

    const opportunities = computed(() => [
      { count: topSellProducts.loading ? "…" : topSellProducts.data?.length || 0, title: "Regulars to review", description: "Revisit products that consistently perform for your account.", to: "/trending", tone: "sand" },
      { count: trendingAcrossChain.loading ? "…" : trendingAcrossChain.data.length, title: "Gaining attention", description: "Eligible products currently showing strong momentum.", to: "/trending", tone: "sage" },
      { count: newToCatalog.loading ? "…" : newToCatalog.data.length, title: "Newly available", description: "Products recently added to the assortment you can order.", to: "/collection/new-arrivals", tone: "blue" },
      { count: priceDrops.loading ? "…" : priceDrops.data.length, title: "Better pricing", description: "Current clearance opportunities inside your catalog.", to: "/clearance", tone: "rose" },
    ]);

    const productSections = computed(() => [
      { id: "reorder-regulars", kicker: "Built around your business", title: "Reorder your regulars", description: "Start with proven products already available to this account.", badge: "Regular", signal: "Ready to review", to: "/trending", loading: topSellProducts.loading, items: topSellProducts.data || [], emptyTitle: "No reorder suggestions yet", emptyText: "Suggestions will improve as purchasing history builds." },
      { id: "trending-across-chain", kicker: "Chain intelligence", title: "Trending across your chain", description: "See eligible products gaining momentum across related stores.", badge: "Trending", signal: "Growing across stores", to: "/trending", loading: trendingAcrossChain.loading, items: trendingAcrossChain.data, emptyTitle: "No chain trends available", emptyText: "New signals will appear as store activity changes." },
      { id: "new-to-catalog", kicker: "Recently approved", title: "New to your catalog", description: "Discover products newly available within your approved assortment.", badge: "New", signal: "Recently made available", to: "/collection/new-arrivals", loading: newToCatalog.loading, items: newToCatalog.data, emptyTitle: "Nothing new right now", emptyText: "Newly approved products will appear here first." },
    ]);

    const catalogCategories = computed(() => (categories.data || []).slice(0, 6));
    const availableBrands = computed(() => {
      const products = [
        ...(topSellProducts.data || []),
        ...trendingAcrossChain.data,
        ...newToCatalog.data,
        ...popularOtherLocations.data,
        ...priceDrops.data,
      ];
      return [...new Set(products.map(getBrand).filter(Boolean))].slice(0, 12);
    });

    const slugUrl = (slug) => `${process.env.BASE_URL || "/"}${slug}`;
    function getBrand(item) { return item?.brand?.name || item?.brand || "Available to you"; }
    function getImage(item) {
      if (!item) return "/images/no-image.png";
      return item.image_url || item.variants?.[0]?.ImageThumbnailUrl || "/images/no-image.png";
    }
    function gotToPage(item, position, listId) {
      if (!item?.slug) return;
      const wholesalePrice = item.variants?.length
        ? Math.max(...item.variants.map((variant) => variant.wholesale_price || 0)).toFixed(2)
        : "0.00";
      const list = { id: listId, type: "route-landing", price: wholesalePrice };
      store.dispatch("setList", { list_id: list.id, list_type: list.type, position: position + 1 });
      ProductClicked(router, item, position + 1, list);
      router.push(`/products/${item.slug}`);
    }

    return {
      accountName, availableBrands, catalogCategories, categories, getBrand, getImage,
      gotToPage, greeting, metrics, opportunities, popularOtherLocations, priceDrops,
      productSections, slugUrl,
    };
  },
  methods: {
    imageBaseUrl(image) {
      if (!image) return "/images/no-image.png";
      const baseUrl = process.env.BASE_URL_IMG || "/";
      try {
        const path = typeof image === "object" ? image.path : JSON.parse(image).path;
        return `${baseUrl}/${path}`;
      } catch (error) {
        return typeof image === "string" ? image : "/images/no-image.png";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.route-dashboard {
  --route-primary: #bd966f;
  --route-primary-light: #fff8f2;
  --route-secondary: #2b303f;
  --route-dark: #485a5e;
  --route-body: #9ea3aa;
  --route-border: #d1d5d7;
  --route-bg: #f9f9f9;
  --route-green-light: #e0ece3;
  --route-purple-light: #f7f8ff;
  --route-red-light: rgba(199, 107, 123, 0.1);
  --route-shadow-box: 0 4px 40px rgba(0, 0, 0, 0.04);
  width: 100%;
  padding-bottom: 56px;
  background: var(--route-bg);
  color: var(--route-secondary);
}
.route-dashboard-inner { display: flex; flex-direction: column; gap: 28px; width: 100%; }

.route-welcome {
  display: flex; align-items: flex-end; justify-content: space-between; gap: 32px;
  padding: 40px; border-radius: 6px;
  background: var(--route-secondary);
  color: #fff; box-shadow: var(--route-shadow-box);
}
.route-eyebrow, .route-section-kicker { display: block; margin-bottom: 8px; color: var(--route-primary); font-size: 11px; font-weight: 600; letter-spacing: 1.4px; text-transform: uppercase; }
.route-welcome .route-eyebrow { color: var(--route-primary); }
.route-welcome h1 { margin: 0; font-size: clamp(28px, 3vw, 42px); font-weight: 600; letter-spacing: -0.8px; line-height: 1.08; }
.route-welcome p { max-width: 660px; margin: 12px 0 0; color: rgba(255, 255, 255, 0.74); font-size: 15px; font-weight: 200; line-height: 1.5; }
.route-primary-action { display: inline-flex; flex: 0 0 auto; min-height: 44px; align-items: center; justify-content: center; gap: 12px; padding: 0 18px; border-radius: 6px; background: #fff; color: var(--route-secondary); font-size: 13px; font-weight: 600; text-transform: uppercase; transition: opacity 160ms ease; }
.route-primary-action:hover { opacity: 0.95; }

.route-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
.route-metric { display: flex; min-height: 132px; flex-direction: column; justify-content: center; padding: 22px 24px; border-radius: 6px; background: #fff; box-shadow: var(--route-shadow-box); }
.route-metric-value { color: var(--route-secondary); font-size: 30px; font-weight: 600; line-height: 1; }
.route-metric-label { margin-top: 10px; font-size: 13px; font-weight: 600; }
.route-metric-note { margin-top: 4px; color: var(--route-body); font-size: 11px; font-weight: 200; }

.route-opportunities, .route-product-section, .route-catalog-explore, .route-brands, .route-chain-panel, .route-price-panel { padding: 30px; border-radius: 6px; background: #fff; box-shadow: var(--route-shadow-box); }
.route-section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
.route-section-heading h2, .route-chain-panel h2, .route-price-panel h2, .route-brands h2 { margin: 0; color: var(--route-secondary); font-size: clamp(22px, 2vw, 28px); font-weight: 600; letter-spacing: -0.35px; line-height: 1.15; }
.route-section-heading p, .route-chain-intro, .route-brands p { margin: 7px 0 0; color: var(--route-body); font-size: 13px; font-weight: 200; line-height: 1.45; }
.route-text-link { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 8px; color: var(--route-body); font-size: 12px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; }
.route-text-link:hover { color: var(--route-secondary); }

.route-opportunity-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
.route-opportunity { position: relative; display: flex; min-height: 172px; flex-direction: column; justify-content: space-between; gap: 20px; padding: 22px; overflow: hidden; border-radius: 6px; color: var(--route-secondary); transition: opacity 160ms ease; }
.route-opportunity:hover { opacity: 0.9; }
.route-opportunity.is-sand { background: var(--route-primary-light); } .route-opportunity.is-sage { background: var(--route-green-light); }
.route-opportunity.is-blue { background: var(--route-purple-light); } .route-opportunity.is-rose { background: var(--route-red-light); }
.route-opportunity-count { font-size: 34px; font-weight: 600; line-height: 1; }
.route-opportunity h3 { margin: 0; font-size: 14px; font-weight: 600; }
.route-opportunity p { margin: 6px 0 0; color: var(--route-dark); font-size: 11px; font-weight: 200; line-height: 1.4; }
.route-opportunity-arrow { position: absolute; top: 20px; right: 20px; font-size: 18px; }

.route-product-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 14px; }
.route-product-card { position: relative; display: flex; min-width: 0; flex-direction: column; align-items: flex-start; padding: 12px; border: 1px solid rgba(209, 213, 215, 0.6); border-radius: 6px; background: #fff; text-align: left; transition: border-color 160ms ease, box-shadow 160ms ease; }
.route-product-card:hover { border-color: var(--route-primary); box-shadow: var(--route-shadow-box); }
.route-product-badge { position: absolute; z-index: 1; top: 18px; left: 18px; padding: 4px 7px; border-radius: 999px; background: rgba(255, 255, 255, 0.94); color: var(--route-dark); font-size: 9px; font-weight: 600; letter-spacing: 0.65px; text-transform: uppercase; }
.route-product-image { display: flex; width: 100%; aspect-ratio: 1 / 0.84; align-items: center; justify-content: center; margin-bottom: 12px; border-radius: 6px; background: var(--route-bg); }
.route-product-image img { width: 82%; height: 82%; object-fit: contain; }
.route-product-brand { width: 100%; overflow: hidden; color: var(--route-primary); font-size: 9px; font-weight: 600; letter-spacing: 0.65px; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.route-product-card strong { display: -webkit-box; min-height: 34px; margin-top: 5px; overflow: hidden; color: var(--route-secondary); font-size: 12px; font-weight: 500; line-height: 1.35; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.route-product-signal { margin-top: 9px; color: var(--route-body); font-size: 10px; font-weight: 200; }
.route-product-skeleton { padding: 12px; border: 1px solid rgba(209, 213, 215, 0.6); border-radius: 6px; }
.route-product-skeleton span { display: block; height: 10px; margin-top: 8px; border-radius: 5px; background: var(--route-bg); animation: route-pulse 1.4s ease-in-out infinite; }
.route-product-skeleton span:first-child { height: 140px; margin: 0; } .route-product-skeleton span:last-child { width: 60%; }
.route-empty-state, .route-inline-empty { display: flex; min-height: 150px; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border: 1px dashed var(--route-border); border-radius: 6px; color: var(--route-body); font-size: 12px; text-align: center; }
.route-empty-state strong { color: var(--route-secondary); font-size: 13px; }

.route-split-sections { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.route-chain-intro { margin-bottom: 22px; }
.route-chain-list, .route-price-list { display: flex; flex-direction: column; gap: 8px; }
.route-chain-list button, .route-price-list button { display: grid; grid-template-columns: 58px minmax(0, 1fr) auto; align-items: center; gap: 13px; width: 100%; padding: 9px; border-radius: 6px; text-align: left; transition: background 160ms ease; }
.route-chain-list button:hover, .route-price-list button:hover { background: var(--route-bg); }
.route-chain-image, .route-price-image { display: flex; width: 58px; height: 58px; align-items: center; justify-content: center; border-radius: 6px; background: var(--route-bg); }
.route-chain-image img, .route-price-image img { width: 85%; height: 85%; object-fit: contain; }
.route-chain-list small, .route-price-list small, .route-chain-list strong, .route-price-list strong, .route-chain-list em, .route-price-list em { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.route-chain-list small, .route-price-list small { color: var(--route-primary); font-size: 9px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; }
.route-chain-list strong, .route-price-list strong { margin-top: 4px; color: var(--route-secondary); font-size: 12px; font-weight: 500; }
.route-chain-list em, .route-price-list em { margin-top: 3px; color: var(--route-body); font-size: 10px; font-style: normal; font-weight: 200; }
.route-inline-empty { min-height: 92px; padding: 18px; }

.route-category-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 18px; }
.route-category { display: flex; min-width: 0; flex-direction: column; align-items: center; gap: 10px; text-align: center; }
.route-category > span, .route-category-skeleton { display: flex; width: 112px; height: 112px; align-items: center; justify-content: center; border-radius: 50%; background: #eee5db; transition: opacity 160ms ease; }
.route-category:hover > span { opacity: 0.85; }
.route-category img { width: 74%; height: 74%; object-fit: contain; }
.route-category strong { width: 100%; overflow: hidden; color: var(--route-secondary); font-size: 11px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.route-category-skeleton { animation: route-pulse 1.4s ease-in-out infinite; }

.route-brands { display: grid; grid-template-columns: minmax(220px, 0.8fr) minmax(0, 2fr); align-items: center; gap: 36px; }
.route-brand-list { display: flex; flex-wrap: wrap; gap: 9px; }
.route-brand-list span { padding: 9px 13px; border: 1px solid rgba(209, 213, 215, 0.7); border-radius: 999px; background: #fff; color: var(--route-secondary); font-size: 11px; font-weight: 500; }
.route-brand-list .route-brand-placeholder { color: var(--route-body); font-weight: 200; }

@keyframes route-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
@media (max-width: 1199px) {
  .route-product-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .route-opportunity-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .route-category-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 767px) {
  .route-dashboard { padding-bottom: 32px; } .route-dashboard-inner { gap: 16px; }
  .route-welcome { align-items: flex-start; flex-direction: column; padding: 26px 22px; border-radius: 6px; }
  .route-welcome h1 { font-size: 28px; } .route-welcome p { font-size: 13px; } .route-primary-action { width: 100%; }
  .route-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; }
  .route-metric { min-height: 118px; padding: 17px; } .route-metric-value { font-size: 26px; } .route-metric-label { font-size: 12px; }
  .route-opportunities, .route-product-section, .route-catalog-explore, .route-brands, .route-chain-panel, .route-price-panel { padding: 22px 18px; border-radius: 6px; }
  .route-section-heading { align-items: flex-start; flex-direction: column; gap: 12px; }
  .route-opportunity-grid { grid-template-columns: 1fr; } .route-opportunity { min-height: 145px; }
  .route-product-grid { display: flex; width: calc(100% + 18px); padding-right: 18px; overflow-x: auto; scroll-snap-type: x mandatory; }
  .route-product-card, .route-product-skeleton { flex: 0 0 164px; scroll-snap-align: start; }
  .route-split-sections { grid-template-columns: 1fr; gap: 16px; }
  .route-category-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px 8px; }
  .route-category > span, .route-category-skeleton { width: 78px; height: 78px; }
  .route-brands { grid-template-columns: 1fr; gap: 20px; }
}
@media (prefers-reduced-motion: reduce) {
  .route-product-card, .route-primary-action, .route-opportunity, .route-category > span { transition: none; }
  .route-product-skeleton span, .route-category-skeleton { animation: none; }
}
</style>
